import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  selectWaiterAuth,
  readPersistedWaiterAuth,
} from '@/features/waiter/waiterSlice';
import {
  setTableSession,
  rehydrateTableSession,
  clearTableSession,
  selectTableSession,
  selectIsInDineIn,
  selectGuestToken,
} from './tableSessionSlice';
import {
  useResolveTableQuery,
  useJoinTableSessionMutation,
} from './tableSessionApi';
import { clearRestaurantCart, setBuffetBudget } from '@/features/cart/cartSlice';
import { setCustomerNote } from '@/features/checkout/checkoutSlice';

// ---------------------------------------------------------------------------
// Step states. A fresh scan that will CREATE the session first shows the
// required headcount picker ('covers'); scanning an already-open session joins
// straight through to the menu. The remaining steps cover the cases where
// joining can't happen yet (loading, waiting for staff-open, error).
// ---------------------------------------------------------------------------
type Step = 'loading' | 'joining' | 'covers' | 'error' | 'wait_staff_open';

interface ErrorState {
  code: string;
  messageKey: string;
  // Optional override for the error heading. Defaults to the generic
  // "table not found / invalid QR" title; set it when the QR is actually valid
  // (e.g. sur-place ordering disabled) so the title isn't misleading.
  titleKey?: string;
  // SC-209: hide the technical "Code : XXX" line for expected business states
  // (e.g. sur-place disabled) where the code is noise to the customer. Left
  // unset for genuine errors (server/connection) where it aids support.
  hideCode?: boolean;
}

// SC-199: pull the throttle retry delay (seconds) out of a 429 body. Returns
// null when absent or non-positive so the UI degrades to the static message.
function readRetryAfter(data: unknown): number | null {
  const ra = (data as { retry_after?: unknown } | undefined)?.retry_after;
  return typeof ra === 'number' && ra > 0 ? Math.ceil(ra) : null;
}

// ---------------------------------------------------------------------------
// TableLanding
// ---------------------------------------------------------------------------
export default function TableLanding() {
  const { t } = useTranslation();
  const { restaurantUuid = '', tableToken = '' } = useParams<{
    restaurantUuid: string;
    tableToken: string;
  }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const existingSession = useAppSelector(selectTableSession);
  // A waiter-opened table URL carries the table's UUID (not a qrcode). When its
  // session auto-clears, this landing can't resolve the UUID on the guest path
  // and would dead-end on "table not found" — unless the device is a logged-in
  // waiter, who is redirected back to their tables overview instead (see the
  // NOT_FOUND branch below). Waiter auth survives reload in localStorage.
  const waiterAuthInStore = useAppSelector(selectWaiterAuth);
  // SC-692: la lecture reste LOCALE. Publier l'auth serveur dans le store depuis
  // ce parcours (ce que faisait un dispatch(rehydrate)) la rendait visible de
  // toute l'application : sur un appareil qui a deja servi de carnet de salle,
  // le client qui scanne son QR heritait des outils du personnel (popup de
  // validation, commentaire de plat). Seule la redirection ci-dessous en a
  // besoin, et elle s'en contente.
  const waiterAuth = useMemo(
    () => waiterAuthInStore ?? readPersistedWaiterAuth(restaurantUuid),
    [waiterAuthInStore, restaurantUuid],
  );
  const isInDineIn = useAppSelector(selectIsInDineIn);
  // Fix 7a: read the stored guest token so we can pass it to the rejoin call
  const storedGuestToken = useAppSelector(selectGuestToken);
  const hasMatchingSession =
    isInDineIn &&
    existingSession?.restaurantUuid === restaurantUuid &&
    existingSession.tableToken === tableToken;

  // Rehydrate the stored session on mount (localStorage, DF-0259: a QR rescan
  // lands in a new tab and must find the same guest token).
  useEffect(() => {
    if (!isInDineIn && restaurantUuid) {
      dispatch(rehydrateTableSession(restaurantUuid));
    }
  }, [dispatch, restaurantUuid, isInDineIn]);

  const [step, setStep] = useState<Step>('loading');
  const [error, setError] = useState<ErrorState | null>(null);
  // SC-199: seconds left before a throttled (429) scan can be retried. null when
  // not throttled or the server didn't send a retry_after (older server).
  const [retrySeconds, setRetrySeconds] = useState<number | null>(null);
  // Guards the one-shot auto-join so a re-render of the resolve effect can't
  // fire a second join. Reset on retry / staff-open so a later attempt re-fires.
  const joinFiredRef = useRef(false);
  // Required headcount for a session-creating scan. Seeded to the table capacity
  // (mirrors the waiter default) when the picker opens; the guest can adjust it.
  const [coversChoice, setCoversChoice] = useState<number | null>(null);

  // SC-199: tick the throttle countdown down to zero (one timer per second).
  useEffect(() => {
    if (retrySeconds === null || retrySeconds <= 0) return;
    const id = setTimeout(() => {
      setRetrySeconds((s) => (s === null ? null : s - 1));
    }, 1000);
    return () => clearTimeout(id);
  }, [retrySeconds]);

  // Step 1: side-effect-free resolve
  const {
    data: resolveData,
    error: resolveError,
    isLoading: resolveLoading,
    // SC-503: also watch `isFetching`, which covers a refetch ("Réessayer");
    // `isLoading` is first-load only. Without it the resolve effect below never
    // re-runs when a retry returns the SAME payload (RTK Query hands back the
    // referentially-identical object), leaving the guest on "Chargement…".
    isFetching: resolveFetching,
    refetch: refetchResolve,
  } = useResolveTableQuery(
    { restaurantUuid, tableToken },
    {
      // Skip only when the active session belongs to this exact QR token.
      skip: hasMatchingSession,
    },
  );

  const [joinTableSession] = useJoinTableSessionMutation();

  // Step 2: auto-join. Fires once when resolve says the session is joinable.
  const performJoin = useCallback(async (coversCount: number) => {
    setStep('joining');
    try {
      // Fix 7a: pass the stored guest token for idempotent rejoin
      const result = await joinTableSession({
        restaurantUuid,
        tableToken,
        covers: coversCount,
        nickname: undefined,
        existingGuestToken: hasMatchingSession ? (storedGuestToken ?? null) : null,
      }).unwrap();

      // No navigation: setting the session flips the parent TableRoute gate to
      // render the menu at the same /table/:tableToken URL.
      //
      // SC-976 (D9): compare the SESSION, not the scanned token. A merged
      // tablée (5+6) has one session reachable from either table's QR — the
      // same phone scanning 6 then 5 must keep its cart (E-12), even though
      // the token changed. A genuinely different session (a foreign table,
      // or a staff 转台 elsewhere) still clears it.
      const cameFromDifferentSession =
        existingSession != null &&
        existingSession.restaurantUuid === restaurantUuid &&
        existingSession.sessionUuid !== result.session.sessionUuid;
      if (cameFromDifferentSession) {
        dispatch(clearRestaurantCart({ restaurantUuid }));
        dispatch(setBuffetBudget({ restaurantUuid, maxItems: 0 }));
        dispatch(setCustomerNote(''));
      }
      dispatch(setTableSession(result.session));
    } catch (err: unknown) {
      const apiErr = err as { status?: number | string; data?: Record<string, unknown> };
      const code = (apiErr.data?.code as string) || 'JOIN_ERROR';
      const status = apiErr.status;

      if (code === 'STAFF_OPEN_REQUIRED') {
        // Fix 9: join-time STAFF_OPEN_REQUIRED race — fall back to the wait
        // screen and let the guest retry once a waiter opens the table.
        joinFiredRef.current = false;
        setStep('wait_staff_open');
      } else if (code === 'TABLE_FULL') {
        setError({ code, messageKey: 'tableLanding.errors.tableFull' });
        setStep('error');
      } else if (code === 'SUR_PLACE_DISABLED') {
        // The QR and table are valid — only sur-place ordering is turned off.
        // Use a clear, non-alarming message instead of "table not found".
        setError({
          code,
          titleKey: 'tableLanding.errors.surPlaceDisabledTitle',
          messageKey: 'tableLanding.errors.surPlaceDisabledBody',
          hideCode: true,
        });
        setStep('error');
      } else if (code === 'LICENSE_EXPIRED') {
        setError({ code, messageKey: 'tableLanding.errors.serviceUnavailable' });
        setStep('error');
      } else if (status === 429) {
        setRetrySeconds(readRetryAfter(apiErr.data));
        setError({ code: 'TOO_MANY_REQUESTS', messageKey: 'tableLanding.errors.tooManyRequests' });
        setStep('error');
      } else if (typeof status === 'number' && status >= 500) {
        joinFiredRef.current = false;
        setError({ code: 'SERVER_ERROR', messageKey: 'tableLanding.errors.connectionProblem' });
        setStep('error');
      } else if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') {
        // Fix 7a: on typed failure from rejoin, clear stored session and let
        // the guest retry the whole flow.
        dispatch(clearTableSession(restaurantUuid));
        joinFiredRef.current = false;
        setError({ code: String(status), messageKey: 'tableLanding.errors.connectionProblem' });
        setStep('error');
      } else {
        setError({ code, messageKey: 'tableLanding.errors.generic' });
        setStep('error');
      }
    }
  }, [joinTableSession, restaurantUuid, tableToken, storedGuestToken, dispatch, hasMatchingSession, existingSession]);

  // Confirm the required headcount → fire the join with the chosen covers.
  // Fall back to the table capacity (or 1) when the seed effect hasn't set
  // coversChoice yet, so a very fast tap can never no-op the join.
  const confirmCovers = useCallback(() => {
    const fallback =
      typeof resolveData?.capacity === 'number' && resolveData.capacity > 0
        ? resolveData.capacity
        : 1;
    joinFiredRef.current = true;
    void performJoin(coversChoice ?? fallback);
  }, [coversChoice, performJoin, resolveData]);

  useEffect(() => {
    if (hasMatchingSession) return; // handled by TableRoute
    if (resolveLoading || resolveFetching) {
      setStep('loading');
      return;
    }
    if (resolveError) {
      const errRaw = resolveError as { status?: number | string; data?: unknown };
      const status = errRaw.status;
      const errData = errRaw.data as Record<string, unknown> | undefined;
      const code = (errData?.code as string) || '';

      // A waiter-opened table URL uses the table UUID as its token; the guest
      // resolve matches on qrcode, so it 404s once the session auto-clears at
      // TTL (the current-session poll 401/403s → clearTableSession → we land
      // here). Don't dead-end a logged-in waiter on "table not found" — send
      // them to their tables overview to deliberately re-open the (now freed)
      // table, consistent with SC-358's auto-clear. Guests keep the error.
      if (
        (status === 404 || code === 'NOT_FOUND') &&
        waiterAuth &&
        waiterAuth.restaurantUuid === restaurantUuid
      ) {
        navigate(`/${restaurantUuid}/serveur`, { replace: true });
        return;
      }

      // Fix 9: branch error taxonomy
      if (status === 429) {
        setRetrySeconds(readRetryAfter(errData));
        setError({ code: 'TOO_MANY_REQUESTS', messageKey: 'tableLanding.errors.tooManyRequests' });
      } else if (status === 404 || code === 'NOT_FOUND') {
        setError({ code: 'NOT_FOUND', messageKey: 'tableLanding.errors.tableNotFound' });
      } else if (code === 'SUR_PLACE_DISABLED') {
        // The QR and table are valid — only sur-place ordering is turned off.
        setError({
          code,
          titleKey: 'tableLanding.errors.surPlaceDisabledTitle',
          messageKey: 'tableLanding.errors.surPlaceDisabledBody',
          hideCode: true,
        });
      } else if (code === 'LICENSE_EXPIRED') {
        setError({ code, messageKey: 'tableLanding.errors.serviceUnavailable' });
      } else if (typeof status === 'number' && status >= 500) {
        setError({ code: 'SERVER_ERROR', messageKey: 'tableLanding.errors.connectionProblem' });
      } else if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') {
        setError({ code: String(status), messageKey: 'tableLanding.errors.connectionProblem' });
      } else {
        setError({ code: code || 'UNKNOWN', messageKey: 'tableLanding.errors.tableNotFound' });
      }
      setStep('error');
      return;
    }
    if (resolveData) {
      // Fix 3: gate wait screen on staff_open_required && !session_open
      if (resolveData.staff_open_required && !resolveData.session_open) {
        setStep('wait_staff_open');
        return;
      }
      if (resolveData.session_open) {
        // Someone already opened this table → join directly; the opener set the
        // headcount. covers is ignored by the join-existing backend path.
        if (!joinFiredRef.current) {
          joinFiredRef.current = true;
          void performJoin(1);
        }
        return;
      }
      // Fresh scan that will CREATE the session → the guest MUST choose the
      // headcount first (required). The join fires from the picker's confirm.
      if (!joinFiredRef.current) {
        setStep('covers');
      }
    }
  }, [resolveLoading, resolveFetching, resolveError, resolveData, hasMatchingSession, performJoin, waiterAuth, navigate, restaurantUuid]);

  // Seed the covers picker once, when it first opens (default = table capacity).
  useEffect(() => {
    if (step === 'covers' && coversChoice === null) {
      const cap =
        typeof resolveData?.capacity === 'number' && resolveData.capacity > 0
          ? resolveData.capacity
          : 2;
      setCoversChoice(cap);
    }
  }, [step, coversChoice, resolveData]);

  // ---------------------------------------------------------------------------
  // Render — only transient/blocked states have UI. Once a session is active for
  // this restaurant the parent TableRoute gate renders the menu, so render
  // nothing here (also covers the brief post-join frame before the gate swaps).
  // ---------------------------------------------------------------------------
  if (hasMatchingSession) {
    return null;
  }

  if (step === 'loading' || step === 'joining') {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <p>{step === 'joining' ? t('tableLanding.joining') : t('tableLanding.loading')}</p>
      </div>
    );
  }

  // Required headcount picker — shown on a session-creating scan. The guest
  // cannot reach the menu without confirming a count (min 1, bounded by the
  // table's max_covers). The waiter/back office track this exact value.
  if (step === 'covers') {
    const maxCovers =
      typeof resolveData?.max_covers === 'number' && resolveData.max_covers > 0
        ? resolveData.max_covers
        : 20;
    const quickMax = Math.min(6, maxCovers);
    const quickOptions = Array.from({ length: quickMax }, (_, i) => i + 1);
    const selected = coversChoice ?? 1;
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <h2>{t('tableLanding.covers.title')}</h2>
        <p style={{ color: '#888' }}>{t('tableLanding.covers.subtitle')}</p>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '0.5rem',
            margin: '1rem 0',
          }}
        >
          {quickOptions.map((n) => (
            <button
              key={n}
              type="button"
              aria-pressed={selected === n}
              onClick={() => setCoversChoice(n)}
              style={{
                minWidth: '3rem',
                padding: '0.75rem 1rem',
                fontWeight: selected === n ? 700 : 400,
                border: selected === n ? '2px solid var(--mint-deep)' : '1px solid #ccc',
                borderRadius: '0.5rem',
                background: selected === n ? 'var(--mint)' : '#fff',
              }}
            >
              {n}
            </button>
          ))}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            marginBottom: '1rem',
          }}
        >
          <button
            type="button"
            aria-label={t('tableLanding.covers.decrement')}
            onClick={() => setCoversChoice((c) => Math.max(1, (c ?? 1) - 1))}
            style={{ padding: '0.5rem 1rem' }}
          >
            −
          </button>
          <span
            aria-live="polite"
            style={{ minWidth: '2rem', fontSize: '1.25rem', fontWeight: 700 }}
          >
            {selected}
          </span>
          <button
            type="button"
            aria-label={t('tableLanding.covers.increment')}
            onClick={() => setCoversChoice((c) => Math.min(maxCovers, (c ?? 1) + 1))}
            style={{ padding: '0.5rem 1rem' }}
          >
            +
          </button>
        </div>
        <button
          type="button"
          onClick={confirmCovers}
          disabled={selected < 1}
          style={{ padding: '0.75rem 2rem', fontWeight: 700 }}
        >
          {t('tableLanding.covers.confirm')}
        </button>
      </div>
    );
  }

  // Fix 3: wait screen only when staff_open_required && !session_open
  // Shows "Réessayer" + read-only menu link
  if (step === 'wait_staff_open') {
    const tableNum = resolveData?.table_number ?? '';
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <h2>{t('tableLanding.waitStaffOpen.title', { n: tableNum })}</h2>
        <p>{t('tableLanding.waitStaffOpen.body')}</p>
        <p style={{ color: '#888', fontSize: '0.9em' }}>
          {t('tableLanding.waitStaffOpen.hint')}
        </p>
        <button
          type="button"
          onClick={() => {
            joinFiredRef.current = false;
            setStep('loading');
            refetchResolve();
          }}
          style={{ margin: '1rem 0.5rem', padding: '0.75rem 1.5rem' }}
        >
          {t('tableLanding.waitStaffOpen.retry')}
        </button>
        <div>
          <Link to={`/${restaurantUuid}`}>
            {t('tableLanding.waitStaffOpen.menuLink')}
          </Link>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    // SC-199: a throttle (429) is retryable, but only once its countdown has
    // elapsed; while counting down we show the delay and hide the button.
    const isThrottled = error?.code === 'TOO_MANY_REQUESTS';
    const isCountingDown = isThrottled && retrySeconds !== null && retrySeconds > 0;
    const isRetryable =
      error?.code === 'SERVER_ERROR' ||
      error?.code === 'FETCH_ERROR' ||
      error?.code === 'TIMEOUT_ERROR' ||
      (isThrottled && !isCountingDown);
    const bodyText = isCountingDown
      ? t('tableLanding.errors.tooManyRequestsCountdown', { seconds: retrySeconds })
      : error
        ? t(error.messageKey)
        : t('tableLanding.errors.generic');
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <h2>{t(error?.titleKey ?? 'tableLanding.errors.tableNotFound')}</h2>
        <p>{bodyText}</p>
        {error?.code && !error.hideCode && (
          <p style={{ color: '#888', fontSize: '0.85em' }}>
            {t('tableLanding.errors.code', { code: error.code })}
          </p>
        )}
        {isRetryable && (
          <button
            type="button"
            onClick={() => {
              joinFiredRef.current = false;
              setRetrySeconds(null);
              setStep('loading');
              setError(null);
              refetchResolve();
            }}
            style={{ marginTop: '1rem', padding: '0.75rem 1.5rem' }}
          >
            {t('tableLanding.errors.retry')}
          </button>
        )}
      </div>
    );
  }

  return null;
}
